import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import SEO from "../components/SEO";
import { Reveal, Eyebrow } from "../components/Reveal";
import { RateCards, useRates } from "../components/GoldRate";

export default function Jewellery() {
  const [categories, setCategories] = useState([]);
  const { rates, loading, refresh } = useRates();

  useEffect(() => {
    api.get("/jewellery/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="jewellery-page" className="pt-28 sm:pt-36">
      <SEO title="Jewellery — Ramesh Kapoor and Sons Jewellers Pvt. Ltd."
        description="Wholesale gold jewellery in Sarafa Bazar Meerut and Karol Bagh Delhi, plus a retail showroom in Meerut. Gold, diamond, bridal jewellery, coins and silver." />

      <header className="max-w-[1400px] mx-auto px-5 sm:px-8 pb-16">
        <Eyebrow>The Jewellery House</Eyebrow>
        <h1 className="font-serif font-light text-4xl sm:text-6xl tracking-tight text-[#FBF9F5] mt-4 leading-[1.05] max-w-4xl">
          Ramesh Kapoor and Sons <span className="italic text-[#C5A059]">Jewellers Pvt. Ltd.</span>
        </h1>
        <p className="text-base text-[#94A3B8] leading-loose mt-8 max-w-2xl">
          Wholesale is the core of our jewellery business — from our flagship office in Sarafa Bazar, Meerut, and
          our wholesale office in Karol Bagh, Delhi. Our Meerut retail showroom serves families buying for
          weddings, festivals, gifting, anniversaries, and personal occasions.
        </p>
        <div className="flex flex-wrap gap-4 mt-10">
          <Link to="/jewellery/wholesale" data-testid="jw-wholesale-btn"
            className="text-[11px] uppercase tracking-[0.22em] font-semibold bg-[#C5A059] text-[#060A12] px-8 py-4 hover:bg-[#d6b678] transition-colors duration-300">
            Wholesale Trade
          </Link>
          <Link to="/jewellery/retail" data-testid="jw-retail-btn"
            className="text-[11px] uppercase tracking-[0.22em] font-semibold border border-white/25 text-[#FBF9F5] px-8 py-4 hover:border-[#C5A059] hover:text-[#C5A059] transition-colors duration-300">
            Retail &amp; Appointments
          </Link>
        </div>
      </header>

      <section className="bg-[#0B1320] border-y border-[#C5A059]/10 py-20">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          <Reveal>
            <Eyebrow>Live Bullion Desk</Eyebrow>
            <h2 className="font-serif text-3xl sm:text-4xl text-[#FBF9F5] mt-3 mb-10">Today’s Metal Rates</h2>
            <RateCards rates={rates} loading={loading} refresh={refresh} />
          </Reveal>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 py-24">
        <Reveal>
          <Eyebrow>The Stock</Eyebrow>
          <h2 className="font-serif text-3xl sm:text-4xl text-[#FBF9F5] mt-3 mb-4">Jewellery Categories</h2>
          <p className="text-sm text-[#94A3B8] max-w-2xl leading-relaxed mb-12">
            Our stock covers gold jewellery, diamond jewellery, bridal sets, chains, bangles, rings, bracelets,
            earrings, mangalsutras, pendants, gold and silver coins, silver jewellery, and silver products.
          </p>
        </Reveal>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-px bg-white/5 border border-white/5">
          {categories.map((c, i) => (
            <Link key={c.slug} to={`/jewellery/category/${c.slug}`} data-testid={`category-${c.slug}`}
              className="group bg-[#060A12] p-6 sm:p-8 hover:bg-[#0B1320] transition-colors duration-500">
              <span className="font-serif text-2xl text-[#C5A059]/25 group-hover:text-[#C5A059]/60 transition-colors duration-500">
                {String(i + 1).padStart(2, "0")}
              </span>
              <h3 className="font-serif text-xl sm:text-2xl text-[#FBF9F5] mt-3 group-hover:text-[#C5A059] transition-colors duration-500">
                {c.name}
              </h3>
            </Link>
          ))}
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 pb-20">
        <Reveal>
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#94A3B8] font-semibold mb-5">Jewellery Guides</p>
          <div className="flex flex-wrap gap-x-8 gap-y-3 text-sm">
            {[
              ["/jewellery-meerut", "Jewellery in Meerut"],
              ["/wholesale-jewellery-meerut", "Wholesale Jewellery in Meerut"],
              ["/jewellery-manufacturer-meerut", "Jewellery Manufacturing"],
              ["/gold-jewellery-meerut", "Gold Jewellery"],
              ["/diamond-jewellery-meerut", "Diamond Jewellery"],
              ["/bridal-jewellery-meerut", "Bridal Jewellery"],
            ].map(([to, label]) => (
              <Link key={to} to={to} data-testid={`guide-${to.slice(1)}`}
                className="text-[#94A3B8] hover:text-[#C5A059] transition-colors duration-300 underline decoration-white/15 underline-offset-4 hover:decoration-[#C5A059]/60">
                {label}
              </Link>
            ))}
          </div>
        </Reveal>
      </section>

      <section className="bg-[#FBF9F5] text-[#1E232A] py-24">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 grid grid-cols-1 md:grid-cols-2 gap-6">
          <Reveal className="border border-[#1E232A]/15 p-10 sm:p-14">
            <Eyebrow light>For the Trade</Eyebrow>
            <h3 className="font-serif text-3xl mt-3">Wholesale — Meerut &amp; Delhi</h3>
            <p className="text-sm text-[#1E232A]/70 leading-loose mt-5">
              Jewellers and retailers can request our catalogue, filter stock by weight and purity, and book a
              meeting at our Sarafa Bazar or Karol Bagh offices.
            </p>
            <Link to="/jewellery/wholesale" data-testid="jw-wholesale-card-btn"
              className="inline-block mt-8 text-[11px] uppercase tracking-[0.2em] font-semibold bg-[#1E232A] text-[#FBF9F5] px-8 py-4 hover:bg-[#C5A059] hover:text-[#060A12] transition-colors duration-300">
              Enter Wholesale
            </Link>
          </Reveal>
          <Reveal delay={0.1} className="border border-[#1E232A]/15 p-10 sm:p-14">
            <Eyebrow light>For Families</Eyebrow>
            <h3 className="font-serif text-3xl mt-3">Retail Showroom — Meerut</h3>
            <p className="text-sm text-[#1E232A]/70 leading-loose mt-5">
              Select jewellery according to your budget, occasion, preferred design, and weight. Book an
              appointment and our team will prepare a selection before your visit.
            </p>
            <Link to="/jewellery/appointment" data-testid="jw-appointment-card-btn"
              className="inline-block mt-8 text-[11px] uppercase tracking-[0.2em] font-semibold border border-[#1E232A]/30 px-8 py-4 hover:border-[#C5A059] hover:text-[#C5A059] transition-colors duration-300">
              Book an Appointment
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
